import * as React from 'react';

import type {
  AIMessageDraft,
  AudienceSource,
} from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import useAudienceCount from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceCount';
import type { LearnerListAudience } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearners';
import useCreateMessageFilter from 'bundles/enterprise-admin-messaging-v2/hooks/useCreateMessageFilter';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

// Human-readable audience label, by source. Program-funnel reminders name the
// resolved count (and sometimes the program); the admin-home recommended actions
// use a FIXED descriptor of the cohort (count-independent, product-defined); a
// learners-table selection uses a plain learner count. Each new funnel audience
// adds its case alongside its `buildClientUserFilter` branch.
const getAudienceSummary = (source: AudienceSource | undefined, count: number): string => {
  if (source?.kind === 'filter') {
    switch (source.spec.audienceType) {
      case 'INVITED_NOT_JOINED':
        return count === 1 ? _t('1 invited learner') : _t('#{count} invited learners', { count });
      case 'JOINED_NOT_ENROLLED':
        return count === 1
          ? _t('1 learner assigned #{programName}', { programName: source.spec.programName })
          : _t('#{count} learners assigned #{programName}', { count, programName: source.spec.programName });
      // Admin-home recommended actions: fixed audience labels (no count).
      case 'ORG_INVITED_NOT_JOINED':
        return _t("Learners who haven't accepted invites");
      case 'ORG_ALL_JOINED':
        return _t('Learners who have joined Coursera');
      case 'ORG_JOINED_NOT_ENROLLED':
        return _t("Learners who haven't enrolled in anything");
      case 'ORG_INACTIVE_LEARNERS':
        return _t('Learners who are inactive');
      case 'ORG_LONG_TERM_INACTIVE_LEARNERS':
        return _t('Learners who have been inactive for a while');
      case 'ORG_ENROLLED_LEARNERS':
      case 'ORG_INCOMPLETE_IN_COURSE':
        return _t("Learners who didn't finish their enrolled courses");
      case 'ORG_RECENT_COMPLETERS':
        return _t('Learners who earned a certificate');
      // Joined learners in the item's target group(s) — the count is meaningful here, so name it.
      case 'JOINED_IN_PROGRAMS':
        return count === 1 ? _t('1 learner') : _t('#{count} learners', { count });
      // Learners assigned a learning path/VSP who haven't finished it yet — the audience for the
      // Assigned Content "remind learners to finish assignment" recommended action.
      case 'ORG_INCOMPLETE_IN_LEARNING_PATH':
        return _t("Learners who haven't finished their assigned content");
      default: {
        const exhaustive: never = source.spec;
        throw new Error(`Unhandled audience spec: ${JSON.stringify(exhaustive)}`);
      }
    }
  }
  return count === 1 ? _t('1 learner') : _t('#{count} learners', { count });
};

type Params = {
  open: boolean;
  autoGenerate: boolean;
  initialPrompt: string;
  audienceSource?: AudienceSource;
  /** The resolved `{ orgId, userFilter }` for the current source (funnel or selection). */
  learnerListAudience?: LearnerListAudience;
  /** Runs AI generation for a prompt (shared with the manual Generate button). */
  runGenerate: (prompt: string) => void;
  updateDraft: (partial: Partial<AIMessageDraft>) => void;
};

/**
 * Drives audience resolution on open for every filter-backed flow. For ANY
 * resolvable audience (funnel `filter` or learners-table `selection`) it fetches
 * the real count via {@link useAudienceCount} and sets it on the draft. For the
 * auto-generate funnel it ALSO kicks off generation and creates the saved
 * `filterId` (so the send-on-review has it) — all in parallel, so the audience
 * never waits on the AI copy. No-op for the generic/manual flow (no audience).
 */
export const useAutoGenerate = ({
  open,
  autoGenerate,
  initialPrompt,
  audienceSource,
  learnerListAudience,
  runGenerate,
  updateDraft,
}: Params): { isAudienceLoading: boolean } => {
  const [isAudienceLoading, setIsAudienceLoading] = React.useState(false);
  const createMessageFilter = useCreateMessageFilter();
  const getAudienceCount = useAudienceCount();

  // Two independent run-once guards (both survive React 18 StrictMode's
  // double-invoked mount effect). They MUST be separate: the modal copies
  // `audienceSource` into state on open, so `learnerListAudience` is undefined on
  // the first open render and only becomes available a render later. A single
  // shared ref would latch on that first render and permanently skip audience
  // resolution (the bug that left the Review screen's audience label blank).
  const generateStartedRef = React.useRef(false);
  const audienceStartedRef = React.useRef(false);
  React.useEffect(() => {
    if (!open) {
      generateStartedRef.current = false;
      audienceStartedRef.current = false;
      return;
    }

    // Fire generation once, as soon as there's a prompt — independent of the
    // audience, which may seed a render later. Fire-and-forget so it runs in
    // parallel with the audience resolution below.
    if (autoGenerate && initialPrompt && !generateStartedRef.current) {
      generateStartedRef.current = true;
      runGenerate(initialPrompt);
    }

    // Resolve the audience once it's available, exactly once per open.
    if (!learnerListAudience || audienceStartedRef.current) return;
    audienceStartedRef.current = true;
    const isFunnel = audienceSource?.kind === 'filter';

    const resolveAudience = async () => {
      setIsAudienceLoading(true);
      try {
        // `getAudienceCount` already resolves null on failure; guard the filter
        // call too so one failure can't drop the other. Only the funnel creates
        // its saved filter on open — the selection creates it at send time.
        const [filterId, count] = await Promise.all([
          isFunnel && audienceSource?.kind === 'filter'
            ? createMessageFilter(audienceSource.spec).catch(() => undefined)
            : Promise.resolve(undefined),
          getAudienceCount(
            learnerListAudience.orgId,
            learnerListAudience.userFilter,
            learnerListAudience.userIdentifiers
          ),
        ]);

        const update: Partial<AIMessageDraft> = {};
        if (filterId) update.filterId = filterId;
        if (count != null) {
          update.audienceCount = count;
          update.audienceSummary = getAudienceSummary(audienceSource, count);
        }
        if (Object.keys(update).length > 0) updateDraft(update);
      } finally {
        setIsAudienceLoading(false);
      }
    };

    resolveAudience();
  }, [
    open,
    autoGenerate,
    initialPrompt,
    audienceSource,
    learnerListAudience,
    runGenerate,
    createMessageFilter,
    getAudienceCount,
    updateDraft,
  ]);

  return { isAudienceLoading };
};

export default useAutoGenerate;
